<template>
  <!-- Reviews Coverage Report Dialog -->
  <BaseDialog
    v-model="showReviewsCoverageDialog"
    title="Reviews Coverage Report"
    :max-width="900"
    toolbar-color="indigo"
    :show-save="false"
  >
    <!-- Date Range Selection -->
    <v-row class="mb-4">
      <v-col cols="12" sm="8">
        <DateRangeFilter
          v-model:from="reviewsCoverageStartDate"
          v-model:to="reviewsCoverageEndDate"
          from-label="Start Date"
          to-label="End Date"
          :max="currentDate"
          density="comfortable"
        />
      </v-col>
      <v-col cols="12" sm="4">
        <v-btn
          color="primary"
          variant="flat"
          @click="fetchReviewsCoverage"
          :loading="reviewsCoverageLoading"
          block
          height="40"
        >
          <v-icon start>mdi-check</v-icon>
          Check Coverage
        </v-btn>
      </v-col>
    </v-row>

    <!-- Quick Date Buttons -->
    <div class="mb-4">
      <v-chip
        v-for="option in quickDateOptions"
        :key="option.label"
        size="small"
        class="mr-2 mb-2"
        @click="selectQuickDate(option)"
        :color="isActiveQuickDate(option) ? 'primary' : 'default'"
        variant="outlined"
      >
        {{ option.label }}
      </v-chip>
    </div>

    <!-- Loading State -->
    <div v-if="reviewsCoverageLoading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <div class="mt-2 text-grey">Loading coverage data...</div>
    </div>

    <!-- Error State -->
    <v-alert v-else-if="reviewsCoverageError" type="error" variant="outlined" class="mb-4">
      {{ reviewsCoverageError }}
    </v-alert>

    <!-- Results -->
    <div v-else-if="reviewsCoverageData">
      <!-- Summary Card -->
      <v-card variant="outlined" class="mb-4">
        <v-card-text class="pa-3">
          <v-row>
            <v-col cols="6" sm="3" class="text-center">
              <div class="text-h5 font-weight-bold">{{ reviewsCoverageData.summary.totalDays }}</div>
              <div class="text-caption text-grey">Total Days</div>
            </v-col>
            <v-col cols="6" sm="3" class="text-center">
              <div class="text-h5 font-weight-bold text-success">
                {{ reviewsCoverageData.summary.completeDays }}
              </div>
              <div class="text-caption text-grey">Complete</div>
            </v-col>
            <v-col cols="6" sm="3" class="text-center">
              <div class="text-h5 font-weight-bold text-warning">
                {{ reviewsCoverageData.summary.incompleteDays }}
              </div>
              <div class="text-caption text-grey">Incomplete</div>
            </v-col>
            <v-col cols="6" sm="3" class="text-center">
              <div class="text-h5 font-weight-bold">
                {{ reviewsCoverageData.summary.averageCoverage }}%
              </div>
              <div class="text-caption text-grey">Avg Coverage</div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Daily Coverage Table -->
      <v-data-table
        :headers="tableHeaders"
        :items="reviewsCoverageData.days"
        :items-per-page="15"
        density="compact"
        class="elevation-1"
      >
        <template v-slot:item.date="{ item }">
          <span class="font-weight-medium">{{ formatLocalDate(item.date) }}</span>
          <span class="text-caption text-grey ml-2">{{ item.dayOfWeek }}</span>
        </template>

        <template v-slot:item.isComplete="{ item }">
          <v-icon
            :color="item.isComplete ? 'success' : item.reported === 0 ? 'grey' : 'warning'"
            size="small"
          >
            {{
              item.isComplete
                ? "mdi-check-circle"
                : item.reported === 0
                  ? "mdi-circle-outline"
                  : "mdi-alert-circle"
            }}
          </v-icon>
        </template>

        <template v-slot:item.coverage="{ item }">
          <span>{{ item.reported }} / {{ item.expected }}</span>
        </template>

        <template v-slot:item.percentage="{ item }">
          <v-chip :color="getPercentageColor(item.percentage)" size="x-small" variant="flat">
            {{ item.percentage }}%
          </v-chip>
        </template>

        <template v-slot:item.missing="{ item }">
          <span v-if="item.missing > 0" class="text-warning">{{ item.missing }} missing</span>
          <span v-else class="text-success">-</span>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn
            v-if="item.allCombinations"
            icon
            size="x-small"
            variant="text"
            color="info"
            @click="showReviewsMissingDetails(item)"
          >
            <v-icon>mdi-information-outline</v-icon>
            <v-tooltip activator="parent" location="top">View details</v-tooltip>
          </v-btn>
        </template>
      </v-data-table>
    </div>

    <!-- Initial State -->
    <div v-else class="text-center py-8 text-grey">
      <v-icon size="64" color="grey-lighten-1">mdi-chart-box-outline</v-icon>
      <div class="mt-2">Select a date range and click "Check Coverage"</div>
    </div>
  </BaseDialog>

  <!-- Coverage Details Dialog -->
  <BaseDialog
    v-model="showReviewsMissingDialog"
    title="Coverage Details"
    :subtitle="selectedReviewDay ? formatLocalDate(selectedReviewDay.date) + ' (' + selectedReviewDay.dayOfWeek + ')' : ''"
    :max-width="600"
    :show-save="false"
  >
    <v-tabs v-model="detailsTab" bg-color="grey-lighten-4" class="mx-n4 mt-n4 mb-4">
      <v-tab value="missing">
        <v-icon start size="small">mdi-alert-circle</v-icon>
        Missing
        <v-chip size="x-small" color="warning" variant="flat" class="ml-2">
          {{ selectedReviewDay?.missing ?? 0 }}
        </v-chip>
      </v-tab>
      <v-tab value="reported">
        <v-icon start size="small">mdi-check-circle</v-icon>
        Reported
        <v-chip size="x-small" color="success" variant="flat" class="ml-2">
          {{ selectedReviewDay?.reported ?? 0 }}
        </v-chip>
      </v-tab>
    </v-tabs>

    <v-data-table
      :headers="detailsTableHeaders"
      :items="detailsTab === 'missing' ? missingCombinations : reportedCombinations"
      :sort-by="[{ key: 'platform', order: 'asc' }]"
      density="compact"
      class="elevation-1"
      :items-per-page="-1"
      hide-default-footer
    >
      <template v-slot:item.copies="{ item }">
        <span :class="item.copiedCount >= item.num_reviews ? 'text-success' : item.copiedCount > 0 ? 'text-warning' : ''">
          {{ item.copiedCount }} / {{ item.num_reviews }}
        </span>
      </template>
      <template v-slot:no-data>
        <div class="text-center text-grey py-4">
          {{ detailsTab === 'missing' ? 'No missing combinations' : 'No reported combinations' }}
        </div>
      </template>
    </v-data-table>
  </BaseDialog>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import BaseDialog from "@/components/BaseDialog.vue";
import DateRangeFilter from "@/components/DateRangeFilter.vue";
import api from "@/utils/api.js";
import { useReviews } from "@/composables/useReviews.js";

const usersMap = ref(new Map());

// Details dialog tab state
const detailsTab = ref("missing");

const {
  showReviewsCoverageDialog,
  reviewsCoverageLoading,
  reviewsCoverageError,
  reviewsCoverageData,
  reviewsCoverageStartDate,
  reviewsCoverageEndDate,
  selectedReviewDay,
  showReviewsMissingDialog,
  currentDate,
  closeReviewsCoverageDialog,
  fetchReviewsCoverage,
  showReviewsMissingDetails,
  closeReviewsMissingDialog,
  getDateNDaysAgo,
} = useReviews();

// Fetch users when dialog opens
watch(showReviewsCoverageDialog, async (newVal) => {
  if (newVal && usersMap.value.size === 0) {
    try {
      const res = await api.authApi.get("/api/admin/users");
      const map = new Map();
      for (const u of res.data || []) {
        const name = [u.first_name, u.last_name].filter(Boolean).join(" ");
        map.set(u.id, name || u.email);
      }
      usersMap.value = map;
    } catch (err) {
      console.error("Failed to fetch users for coverage:", err);
    }
  }
});

// Auto-set end date when start date is entered
watch(reviewsCoverageStartDate, (newVal) => {
  if (newVal && !reviewsCoverageEndDate.value) {
    reviewsCoverageEndDate.value = newVal;
  }
});

// Reset tab to "missing" when dialog opens
watch(showReviewsMissingDialog, (newVal) => {
  if (newVal) {
    detailsTab.value = "missing";
  }
});

// Computed: filter combinations by missing/reported
const allCombinations = computed(() => {
  return selectedReviewDay.value?.allCombinations || [];
});

const withUserName = (combos) =>
  combos.map((c) => ({
    ...c,
    user: usersMap.value.get(c.assigned_user_id) || "",
  }));

const missingCombinations = computed(() => {
  const all = allCombinations.value.filter((c) => c.isMissing);
  return withUserName(all);
});

const reportedCombinations = computed(() => {
  return withUserName(allCombinations.value.filter((c) => c.copiedCount > 0));
});

// Details table headers (no City column for reviews)
const detailsTableHeaders = [
  { title: "Platform", key: "platform", sortable: true },
  { title: "Location", key: "location", sortable: true },
  { title: "Brand", key: "brand", sortable: true },
  { title: "Copies", key: "copies", sortable: true, align: "center" },
  { title: "User", key: "user", sortable: true },
];

// Table headers
const tableHeaders = [
  { title: "Date", key: "date", sortable: true },
  { title: "Status", key: "isComplete", sortable: true, align: "center" },
  { title: "Coverage %", key: "percentage", sortable: true, align: "center" },
  { title: "Coverage", key: "coverage", sortable: false },
  { title: "Missing", key: "missing", sortable: true },
  { title: "", key: "actions", sortable: false, width: "50px" },
];

// Quick date options
const quickDateOptions = computed(() => [
  { label: "Today", start: currentDate, end: currentDate },
  { label: "Yesterday", start: getDateNDaysAgo(1), end: getDateNDaysAgo(1) },
  { label: "Last 7 Days", start: getDateNDaysAgo(7), end: currentDate },
  { label: "Last 14 Days", start: getDateNDaysAgo(14), end: currentDate },
  { label: "Last 30 Days", start: getDateNDaysAgo(30), end: currentDate },
]);

function isActiveQuickDate(option) {
  return option.start === reviewsCoverageStartDate.value && option.end === reviewsCoverageEndDate.value;
}

function selectQuickDate(option) {
  reviewsCoverageStartDate.value = option.start;
  reviewsCoverageEndDate.value = option.end;
}

function getPercentageColor(percentage) {
  if (percentage === 100) return "success";
  if (percentage >= 80) return "warning";
  if (percentage > 0) return "error";
  return "grey";
}

function formatLocalDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString + "T00:00:00");
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
</script>

<style scoped>
.border-t {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
